import { cn } from 'helpers/classnames';
import ImageLazyLoader from 'components/common/ImageLazyLoader';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import { makeHashFromString } from 'helpers/index';

import css from 'styles/components/landing/landingPageImage.scss';

// static media queries are ok, they align with what is returned in the data.
// this is somewhat temporary, as we redesign image components to match Figma, we'd prefer static aspect ratios rather than being so dynamic.

const makeAspectRatioStyleInnerHtml = ({ id, sources, img }) => {
  let mediaStyles = '';
  for (const srcdef of sources || []) {
    if (srcdef?.aspectratio && srcdef?.media) {
      mediaStyles += ` @media ${srcdef.media} { #${id} { aspect-ratio: ${srcdef.aspectratio}; } }`;
    }
  }

  if (mediaStyles || img?.aspectratio) {
    let style = `#${id} { object-fit: cover; `;
    if (img?.aspectratio) {
      style += `aspect-ratio: ${img.aspectratio}; }`;
    } else {
      style += '}';
    }
    return style + mediaStyles;
  }
  return undefined;
};

/**
 * Takes a sources array from ZCS, and other properties to construct a sources array
 *
 * @param sources
 * @param mobilesrc
 * @param mobileretina
 * @param tabletsrc
 * @param tabletretina
 * @param srcset
 * @param src
 * @param retina
 * @param srcX
 * @param srcY
 * @param retinaX
 * @param retinaY
 * @return {{nonDefaultSources, defaultSource}}
 */
function extractDefaultAndSources(sources, mobilesrc, mobileretina, tabletsrc, tabletretina, srcset, src, retina, srcX, srcY, retinaX, retinaY) {
  const srcsetFrom = s => {
    let ret = '';
    for (const [img, spec] of s) {
      if (img) {
        if (ret) {
          ret += ', ';
        }
        ret += img;
        if (spec) {
          ret += ' ';
          ret += spec;
        }
      }
    }
    return ret;
  };
  const aspectRatioFrom = o => {
    const stdAspect = o?.std?.x && o?.std?.y && o?.std?.x / o?.std?.y;
    const retAspect = o?.ret?.x && o?.ret?.y && o?.ret?.x / o?.ret?.y;
    return stdAspect &&
      retAspect && // if both are present
      (stdAspect < 1 && retAspect < 1
        ? Math.abs(1 / stdAspect - 1 / retAspect) > 0.02 // invert and compare if both less than one.
        : Math.abs(stdAspect - retAspect) > 0.02) // otherwise just compare
      ? undefined // aspect ratios are different,  we can not apply an aspect ratio
      : stdAspect || retAspect; // aspect ratios are the same, we can apply the aspect ratio
  };

  // if the sources array does not exist, build it.
  if (!sources?.length) {
    // ZCS provides sources now, should very rarely get here.
    sources = [];
    if (mobilesrc || mobileretina) {
      sources.push({
        media: `(max-width: 650px)`,
        srcset: srcsetFrom([
          [mobilesrc, '1x'],
          [mobileretina, '2x']
        ])
      });
    }
    if (tabletsrc || tabletretina) {
      sources.push({
        media: `(max-width: 1024px)`,
        srcset: srcsetFrom([
          [tabletsrc, '1x'],
          [tabletretina, '2x']
        ])
      });
    }
  }

  let defaultSource = null;
  for (const source of sources) {
    if (!source.media) {
      defaultSource = source;
      break;
    }
  }

  const nonDefaultSources = [];
  for (const source of sources) {
    if (source.media) {
      nonDefaultSources.push(source);
    }
  }

  // if no default entry exits, then use the older "desktop src"
  if (!defaultSource) {
    defaultSource = {};
  }

  // if we do not have a default source in the sources array, then
  // create one from the older variables.  This is for the ZCS transition period
  // where we add the desktop image into sources.
  if (!defaultSource.srcset) {
    defaultSource.srcset =
      srcset ||
      srcsetFrom([
        [src, '1x'],
        [retina, '2x']
      ]);
    defaultSource.aspectratio = aspectRatioFrom({
      std: { x: srcX, y: srcY },
      ret: { x: retinaX, y: retinaY }
    })?.toFixed(4);
  }

  // if no src element is set, rip the first one out of the default sourceset.
  if (!defaultSource.src) {
    const trimmed = defaultSource?.srcset?.trim();
    const split = trimmed?.split(/\s+/, 1);
    defaultSource.src = split?.[0] || '';
  }
  return { nonDefaultSources, defaultSource };
}

/**
 * Render images with different src files depending on what SiteMerch populates in Symphony
 */
export const LandingPageImage = ({
  src = null,
  srcset = null,
  mobilesrc = null,
  tabletsrc = null,
  retina = null,
  mobileretina = null,
  tabletretina = null,
  sources = null,
  alt = null,
  title = null,
  width = null,
  height = null,
  role = null,
  itemProp = null,
  shouldLazyLoad = false,
  className = null,
  imgTestId = null,
  isFullWidth = false,
  src_x: srcX,
  src_y: srcY,
  retinasrc_x: retinaX,
  retinasrc_y: retinaY,
  fetchPriority
}) => {
  const { nonDefaultSources, defaultSource } = extractDefaultAndSources(
    sources,
    mobilesrc,
    mobileretina,
    tabletsrc,
    tabletretina,
    srcset,
    src,
    retina,
    srcX,
    srcY,
    retinaX,
    retinaY
  );

  // build the media-selector sources.
  const pictureProps = [];
  for (const source of nonDefaultSources) {
    if (source.media && source.srcset) {
      pictureProps.push(<source key={source.srcset + source.media} media={source.media} srcSet={source.srcset} />);
    }
  }

  const imgProps = {
    'src': defaultSource.src,
    title,
    width,
    height,
    'srcSet': defaultSource.srcset,
    role,
    itemProp,
    'alt': !alt || alt === '""' ? '' : alt,
    'data-test-id': imgTestId,
    'fetchpriority': fetchPriority
  };

  if (imgProps.src) {
    /*
     *  * if we are using mobile/tables sources, then compute the aspect ratios, otherwise, don't include them.
     *  * if we have both desktop std and retina sources, then compare the aspect ratios, and use them if they match
     *  * if we have only one std or retina source, then use that aspect ratio
     */
    const imgId = makeHashFromString(imgProps.src);

    // returns null if no style, otherwise returns the aspect ratio style for the layout, if it is defined
    // we now only support the "sources" array for mobile and tablet images.
    const aspectRatioStyle = makeAspectRatioStyleInnerHtml({
      id: imgId,
      sources: nonDefaultSources,
      img: defaultSource
    });

    if (shouldLazyLoad) {
      return (
        <>
          {aspectRatioStyle ? <style dangerouslySetInnerHTML={{ __html: aspectRatioStyle }} /> : undefined}
          <ImageLazyLoader
            id={imgId}
            className={cn(className, { [css.fullWidth]: isFullWidth, [css.hasStyledAspectRatio]: !!aspectRatioStyle })}
            imgProps={{ ...imgProps, id: imgId }}
            pictureProps={pictureProps}
          />
        </>
      );
    } else {
      return (
        <>
          {aspectRatioStyle ? <style dangerouslySetInnerHTML={{ __html: aspectRatioStyle }} /> : undefined}
          <picture className={isFullWidth ? css.fullWidth : null}>
            {pictureProps}
            <img
              id={imgId}
              className={cn({ [css.fullWidth]: isFullWidth, [css.hasStyledAspectRatio]: !!aspectRatioStyle })}
              role={role}
              {...imgProps}
              alt={imgProps.alt}
            />
          </picture>
        </>
      );
    }
  }
  return null;
};

export default withErrorBoundary('LandingPageImage', LandingPageImage);
